import type { IEpubPreferences } from '@readium/navigator';
import { findReaderThemeById } from './reader-themes';

export enum HighContrastMode {
  NONE = 'NONE',
  LIGHT = 'LIGHT',
  DARK = 'DARK',
}

export type HighContrastPreset = Pick<
  IEpubPreferences,
  'backgroundColor' | 'linkColor' | 'textColor'
>;

export const HIGH_CONTRAST_PRESETS: Record<
  HighContrastMode.LIGHT | HighContrastMode.DARK,
  HighContrastPreset
> = {
  [HighContrastMode.LIGHT]: {
    backgroundColor: findReaderThemeById('White')?.backgroundColor,
    textColor: 'rgb(0, 0, 0)',
    linkColor: 'rgb(0, 0, 238)',
  },
  [HighContrastMode.DARK]: {
    backgroundColor: findReaderThemeById('Black')?.backgroundColor,
    textColor: 'rgb(255, 255, 255)',
    linkColor: 'rgb(255, 255, 0)',
  },
};

const LIGHT_CLASS = 'high-contrast-light';
const DARK_CLASS = 'high-contrast-dark';
const HIGH_CONTRAST_LINK_ID = 'high-contrast-link';

const HIGH_CONTRAST_HREFS: Record<HighContrastMode.LIGHT | HighContrastMode.DARK, string> = {
  [HighContrastMode.LIGHT]: 'https://themes.odilo.io/NubeReader_High_Contrast_White/_theme.css',
  [HighContrastMode.DARK]: 'https://themes.odilo.io/NubeReader_High_Contrast_Black/_theme.css',
};

/**
 * Toggles the body class the NubeReader_High_Contrast_White/Black theme stylesheets key
 * off — same approach as ng-management-hub. Those stylesheets override every themed CSS
 * custom property for us (e.g. --primary-background), instead of us tracking each odl-*
 * component's variables by hand.
 *
 * The stylesheet itself is attached/detached here rather than kept permanently in
 * index.html: it sets custom properties at :root, and since it would then load AFTER
 * #theme-link, it would win the cascade over the active reading theme's own colors even
 * while high contrast is off. Only ever having it in the DOM while a mode is active rules
 * that out regardless of how the remote CSS scopes its rules.
 */
export function applyHighContrastMode(mode: HighContrastMode): void {
  const { body, head } = document;
  body.classList.remove(LIGHT_CLASS, DARK_CLASS);

  const existingLink = head.querySelector<HTMLLinkElement>(`#${HIGH_CONTRAST_LINK_ID}`);
  if (mode === HighContrastMode.NONE) {
    existingLink?.remove();
    return;
  }

  body.classList.add(mode === HighContrastMode.LIGHT ? LIGHT_CLASS : DARK_CLASS);
  const href = HIGH_CONTRAST_HREFS[mode];
  if (existingLink) {
    existingLink.href = href;
    return;
  }
  const link = document.createElement('link');
  link.id = HIGH_CONTRAST_LINK_ID;
  link.rel = 'stylesheet';
  link.href = href;
  head.appendChild(link);
}

const STORAGE_KEY = 'NUBEREADER_HIGH_CONTRAST_MODE';

function getOsPreferredHighContrastMode(): HighContrastMode {
  if (!matchMedia('(prefers-contrast: more)').matches) return HighContrastMode.NONE;
  return matchMedia('(prefers-color-scheme: light)').matches
    ? HighContrastMode.LIGHT
    : HighContrastMode.DARK;
}

export function getPersistedHighContrastMode(): HighContrastMode {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    return getOsPreferredHighContrastMode();
  }
  if (
    stored === HighContrastMode.NONE ||
    stored === HighContrastMode.LIGHT ||
    stored === HighContrastMode.DARK
  ) {
    return stored;
  }
  return getOsPreferredHighContrastMode();
}

export function persistHighContrastMode(mode: HighContrastMode): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
    return true;
  } catch {
    return false;
  }
}
